import type { ReactNode } from "react";
import { cx } from "@/utils/cx";

/**
 * StatCard — velká číselná metrika s volitelnou delta + helper textem.
 *
 * Nativní metrics komponenty (MetricsSimple atd.) mají nekompatibilní API
 * (title/subtitle/change/trend + dropdown + charts) — zachováváme legacy
 * {label,value,helper,delta} přes vlastní wrapper na UUI tokenech.
 *
 * Color-not-only (WCAG AA 1.4.1): směr delty nese symbol ↑/↓/— (aria-hidden)
 * + barva, ne jenom barva.
 */

export interface StatCardProps {
    label: string;
    value: ReactNode;
    helper?: ReactNode;
    delta?: {
        direction: "up" | "down" | "flat";
        label: string;
    };
    className?: string;
}

const DIRECTION_SYMBOL: Record<"up" | "down" | "flat", string> = {
    up: "↑",
    down: "↓",
    flat: "—",
};

const deltaColorClasses: Record<"up" | "down" | "flat", string> = {
    up: "text-success-primary",
    down: "text-error-primary",
    flat: "text-tertiary",
};

export function StatCard({ label, value, helper, delta, className }: StatCardProps) {
    return (
        <article
            className={cx(
                "flex flex-col gap-1 rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary",
                className,
            )}
        >
            <span className="text-sm font-medium text-tertiary">{label}</span>
            <span className="text-display-sm font-semibold text-primary">{value}</span>
            {delta && (
                <span data-direction={delta.direction} className={cx("text-sm", deltaColorClasses[delta.direction])}>
                    <span aria-hidden="true">{DIRECTION_SYMBOL[delta.direction]}</span> {delta.label}
                </span>
            )}
            {helper && <span className="text-sm text-tertiary">{helper}</span>}
        </article>
    );
}
